import type Rate from '~/types/Rate';

import { RateAttribute } from '~/types/Rate';

import { FeatureFlags, decideFeatureFlag } from './featureflags';

const DEFAULT_PERCENT_VARIATION = 1.5;

/**
 * [WEB-1816] Set UPS Ground or USPS Priority as the best option when the rate is within the threshold of the lowest rate.
 * @param rates - The current rates to compare.
 * @returns The rates with the best option attribute set.
 */
export const getBestRates = (rates: Rate[] = []): Rate[] => {
  if (rates.length === 0) return [];

  const bestRatesFrequencyFlag = decideFeatureFlag(FeatureFlags.BEST_RATES_FREQUENCY);
  const isBestRatesFrequencyEnabled = bestRatesFrequencyFlag?.enabled ?? false;
  const bestRatesThreshold = bestRatesFrequencyFlag?.variables?.best_rates_threshold;

  const percentVariation = isBestRatesFrequencyEnabled
    ? bestRatesThreshold ?? DEFAULT_PERCENT_VARIATION
    : DEFAULT_PERCENT_VARIATION;

  const sortedRates = [...rates].sort((a, b) => Number(a.amount) - Number(b.amount));
  const lowestRate = sortedRates[0];

  const upsGround = rates.find((rate) => rate.servicelevel.token === 'ups_ground');
  const uspsPriority = rates.find((rate) => rate.servicelevel.token === 'usps_priority');

  let bestOption: Rate | null = null;
  if (upsGround && uspsPriority)
    bestOption = Number(upsGround.amount) < Number(uspsPriority.amount) ? upsGround : uspsPriority;
  else bestOption = upsGround || uspsPriority || null;

  if (!bestOption) return rates;

  const target = Number(lowestRate.amount) * percentVariation;
  const isWithinThreshold = Number(bestOption.amount) < target;
  if (isWithinThreshold) bestOption.attributes = [RateAttribute.BEST_OPTION];

  return rates;
};
